/**
 * API request logger for the Debug tab.
 * Keeps a rolling buffer of the last 200 requests.
 * Masks sensitive query parameters (key, token, api_key) in logged URLs.
 */

const MAX_ENTRIES = 200;

export interface RequestLogEntry {
  timestamp: string;
  url: string;
  method: string;
  status: number;
  latencyMs: number;
  error?: string;
}

const entries: RequestLogEntry[] = [];
const listeners = new Set<(entry: RequestLogEntry) => void>();

/** Sensitive query-param names to mask in logged URLs. */
const SENSITIVE_PARAMS = ['key', 'token', 'api_key', 'apikey', 'access_token', 'secret'];

/** Mask sensitive query parameters in a URL string. */
function maskUrl(raw: string): string {
  try {
    const url = new URL(raw);
    for (const param of SENSITIVE_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.set(param, '***');
      }
    }
    return url.toString();
  } catch {
    // Not a valid URL — strip anything that looks like key=VALUE
    return raw.replace(
      /([?&])(key|token|api_key|apikey|access_token|secret)=[^&]*/gi,
      '$1$2=***',
    );
  }
}

/** Log an API request. Keeps last 200 entries. */
export function logRequest(entry: RequestLogEntry): void {
  const masked: RequestLogEntry = {
    ...entry,
    url: maskUrl(entry.url),
  };

  entries.unshift(masked);
  if (entries.length > MAX_ENTRIES) {
    entries.length = MAX_ENTRIES;
  }

  for (const cb of listeners) {
    try {
      cb(masked);
    } catch {
      // Ignore listener errors
    }
  }
}

/** Get all logged entries (newest first). */
export function getRequestLog(): RequestLogEntry[] {
  return entries.slice();
}

/** Clear all logged entries. */
export function clearRequestLog(): void {
  entries.length = 0;
}

/** Subscribe to new log entries. Returns unsubscribe function. */
export function onLogEntry(cb: (entry: RequestLogEntry) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
